import React, { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { Navigation, MapPin, X } from 'lucide-react';
import { useTranslation } from '@/contexts/TranslationContext';

// Fix for default markers in react-leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',  
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

interface AdoptionLocationPickerProps {
  onLocationSelect: (latitude: number, longitude: number, address: string) => void;
  initialLocation?: { latitude: number; longitude: number; address: string };
}

const AdoptionLocationPicker: React.FC<AdoptionLocationPickerProps> = ({
  onLocationSelect,
  initialLocation
}) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<L.Map | null>(null);
  const marker = useRef<L.Marker | null>(null);
  const { toast } = useToast();
  const [selectedLocation, setSelectedLocation] = useState<{
    latitude: number;
    longitude: number;
    address: string;
  } | null>(initialLocation || null);

  useEffect(() => {
    if (!mapContainer.current || map.current) return;

    // Initialize map only once
    map.current = L.map(mapContainer.current, {
      center: initialLocation 
        ? [initialLocation.latitude, initialLocation.longitude]
        : [41.9028, 12.4964], // Rome default
      zoom: initialLocation ? 14 : 10,
      zoomControl: true,
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors'
    }).addTo(map.current);

    // Add click handler
    map.current.on('click', async (e) => {
      const { lat, lng } = e.latlng;
      await setLocationOnMap(lat, lng);
    });

    // Set initial location if provided
    if (initialLocation) {
      setLocationOnMap(initialLocation.latitude, initialLocation.longitude);
    }

    return () => {
      if (map.current) {
        map.current.off();
        map.current.remove();
        map.current = null;
        marker.current = null;
      }
    };
  }, []);

  // Handle initial location updates separately
  useEffect(() => {
    if (initialLocation && map.current) {
      setLocationOnMap(initialLocation.latitude, initialLocation.longitude);
    }
  }, [initialLocation]);

  const setLocationOnMap = async (latitude: number, longitude: number) => {
    if (!map.current) return;

    // Remove existing marker
    if (marker.current) {
      marker.current.remove();
    }

    // Add new marker
    marker.current = L.marker([latitude, longitude], {
      icon: L.divIcon({
        html: `
          <div style="
            background-color: #ec4899;
            width: 32px;
            height: 32px;
            border-radius: 50%;
            border: 3px solid white;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 16px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.3);
          ">
            📍
          </div>
        `,
        className: 'custom-div-icon',
        iconSize: [32, 32],
        iconAnchor: [16, 16]
      })
    }).addTo(map.current);

    // Get readable address using reverse geocoding
    let address = `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
    
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=10&addressdetails=1`
      );
      const data = await response.json();
      
      if (data && data.address) {
        // Build a readable address
        const parts = [];
        if (data.address.city) parts.push(data.address.city);
        else if (data.address.town) parts.push(data.address.town);
        else if (data.address.village) parts.push(data.address.village);
        else if (data.address.municipality) parts.push(data.address.municipality);
        
        if (data.address.province) parts.push(data.address.province);
        else if (data.address.state) parts.push(data.address.state);
        
        if (parts.length > 0) {
          address = parts.join(', ');
        } else if (data.display_name) {
          // Fallback to simplified display name
          const displayParts = data.display_name.split(',');
          address = displayParts.slice(0, 2).join(',').trim();
        }
      }
    } catch (error) {
      console.log('Reverse geocoding failed, using coordinates');
    }
    
    const locationData = {
      latitude,
      longitude,
      address
    };
    
    setSelectedLocation(locationData);
    onLocationSelect(latitude, longitude, address);
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast({
        title: "Geolocalizzazione non supportata",
        description: "Il tuo browser non supporta la geolocalizzazione",
        variant: "destructive",
      });
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        
        if (map.current) {
          map.current.setView([latitude, longitude], 16);
        }
        
        await setLocationOnMap(latitude, longitude);
        
        toast({
          title: "Posizione trovata!",
          description: "Ti abbiamo localizzato sulla mappa",
        });
      },
      (error) => {
        toast({
          title: "Errore geolocalizzazione",
          description: "Non è possibile ottenere la tua posizione",
          variant: "destructive",
        });
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const clearLocation = () => {
    if (marker.current) {
      marker.current.remove();
      marker.current = null;
    }
    
    setSelectedLocation(null);
    onLocationSelect(0, 0, '');
  };

  return (
    <div className="space-y-4">
      <div className="relative">
        <div
          ref={mapContainer}
          className="w-full h-[400px] rounded-lg border border-border"
        />
        
        <Button
          onClick={getCurrentLocation}
          className="absolute top-4 right-4 z-[1000]"
          size="sm"
        >
          <Navigation className="w-4 h-4 mr-2" />
          Usa posizione attuale
        </Button>

        {selectedLocation && (
          <Button
            onClick={clearLocation}
            variant="destructive"
            size="sm"
            className="absolute bottom-4 right-4 z-[1000]"
          >
            <X className="w-4 h-4 mr-2" />
            Cancella
          </Button>
        )}
      </div>

      {selectedLocation && (
        <div className="p-3 bg-muted rounded-lg">
          <div className="flex items-center gap-2 text-sm">
            <MapPin className="w-4 h-4 text-primary" />
            <span className="font-medium">Posizione selezionata:</span>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            {selectedLocation.address}
          </p>
        </div>
      )}

      <p className="text-sm text-muted-foreground">
        Clicca sulla mappa per selezionare la posizione del pet da adottare.
      </p>
    </div>
  );
};

export default AdoptionLocationPicker;